/**
 * Synthesized notification sounds via Web Audio API. Original tones — no
 * audio files shipped or fetched. Browsers block autoplay until the user
 * interacts with the page, so we resume the AudioContext on every play
 * attempt (it's cheap when already running) and keep a listener that
 * primes it after the very first user gesture.
 */

type Ctx = AudioContext;

let ctx: Ctx | null = null;
let primed = false;
const MUTE_KEY = 'erp_notif_sound_muted';

/** Custom URLs override the synthesized sound. Set from the admin settings on
 *  mount — null/empty falls back to synthesis. */
let customOrderUrl: string | null = null;
let customReminderUrl: string | null = null;

export function setCustomSounds(s: { orderUrl?: string | null; reminderUrl?: string | null }): void {
  customOrderUrl = s.orderUrl ?? null;
  customReminderUrl = s.reminderUrl ?? null;
}

function playUrl(url: string): void {
  try {
    const a = new Audio(url);
    a.volume = 0.9;
    a.play().catch(() => { /* autoplay blocked or asset missing — fall back silently */ });
  } catch { /* noop */ }
}

function getCtx(): Ctx | null {
  if (ctx) return ctx;
  const w = window as unknown as {
    AudioContext?: typeof AudioContext;
    webkitAudioContext?: typeof AudioContext;
  };
  const AC = w.AudioContext ?? w.webkitAudioContext;
  if (!AC) return null;
  try { ctx = new AC(); } catch { return null; }
  return ctx;
}

function prime(): void {
  const c = getCtx();
  if (!c) return;
  c.resume().catch(() => { /* noop */ });
  primed = true;
}

if (typeof window !== 'undefined') {
  const onFirst = () => { prime(); };
  document.addEventListener('pointerdown', onFirst, { capture: true });
  document.addEventListener('keydown', onFirst, { capture: true });
  document.addEventListener('touchstart', onFirst, { capture: true });
}

export function isMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === '1'; } catch { return false; }
}

export function setMuted(v: boolean): void {
  try { localStorage.setItem(MUTE_KEY, v ? '1' : '0'); } catch { /* noop */ }
}

/** Fire a short synthesized test ding. Useful for "test sound" UI. */
export function playTest(): void {
  prime();
  playDing();
}

function playNote(
  c: Ctx,
  freq: number,
  when: number,
  attackMs: number,
  peak: number,
  durationMs: number,
  wave: OscillatorType = 'sine',
): void {
  const osc = c.createOscillator();
  const gain = c.createGain();
  osc.type = wave;
  osc.frequency.setValueAtTime(freq, when);
  const attack = attackMs / 1000;
  const duration = durationMs / 1000;
  gain.gain.setValueAtTime(0, when);
  gain.gain.linearRampToValueAtTime(peak, when + attack);
  gain.gain.exponentialRampToValueAtTime(0.0001, when + duration);
  osc.connect(gain);
  gain.connect(c.destination);
  osc.start(when);
  osc.stop(when + duration + 0.05);
}

/** Strike a small metallic bell at the given fundamental, using inharmonic
 *  partials for the characteristic "tring" timbre. */
function strikeBell(c: Ctx, fundamental: number, when: number, peak: number, durationMs: number): void {
  // Inharmonic ratios tuned to sound like a small handbell / cash-register bell.
  const partials: Array<{ ratio: number; gain: number; decay: number }> = [
    { ratio: 1.0,    gain: 1.0,  decay: 1.0  },
    { ratio: 2.0,    gain: 0.55, decay: 0.8  },
    { ratio: 2.756,  gain: 0.4,  decay: 0.65 },
    { ratio: 4.118,  gain: 0.3,  decay: 0.5  },
    { ratio: 5.404,  gain: 0.22, decay: 0.4  },
    { ratio: 6.802,  gain: 0.14, decay: 0.3  },
  ];
  for (const p of partials) {
    playNote(
      c,
      fundamental * p.ratio,
      when,
      3,                              // 3 ms attack for a sharp strike
      peak * p.gain,
      durationMs * p.decay,
      'sine',
    );
  }
  // Very short noise-ish click for the metal-on-metal attack transient.
  const clickDur = 0.02;
  const buf = c.createBuffer(1, Math.floor(c.sampleRate * clickDur), c.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < data.length; i++) {
    const env = 1 - i / data.length;
    data[i] = (Math.random() * 2 - 1) * env * 0.6;
  }
  const src = c.createBufferSource();
  const clickGain = c.createGain();
  const hp = c.createBiquadFilter();
  hp.type = 'highpass';
  hp.frequency.setValueAtTime(2500, when);
  src.buffer = buf;
  clickGain.gain.setValueAtTime(peak * 0.25, when);
  src.connect(hp);
  hp.connect(clickGain);
  clickGain.connect(c.destination);
  src.start(when);
  src.stop(when + clickDur + 0.02);
}

function tryPlay(runner: (c: Ctx, now: number) => void): void {
  if (isMuted()) return;
  const c = getCtx();
  if (!c) return;
  const go = () => {
    const now = c.currentTime + 0.02;
    try { runner(c, now); } catch { /* noop */ }
  };
  if (c.state === 'suspended') {
    c.resume().then(go).catch(() => { /* user hasn't interacted yet */ });
  } else {
    go();
  }
}

/** Simple two-note chime for reminders / low-priority alerts. */
export function playDing(): void {
  if (isMuted()) return;
  if (customReminderUrl) { playUrl(customReminderUrl); return; }
  tryPlay((c, now) => {
    playNote(c, 523.25, now,       10, 0.28, 260, 'sine');
    playNote(c, 659.25, now + 0.1, 10, 0.34, 320, 'sine');
  });
}

/** Cash-register "tring!" — metallic bell, double-strike. */
export function playCashSound(): void {
  if (isMuted()) return;
  if (customOrderUrl) { playUrl(customOrderUrl); return; }
  tryPlay((c, now) => {
    // Two quick strikes of a small bell (~2100 Hz fundamental), 110 ms apart.
    strikeBell(c, 2100, now,         0.32, 1400);
    strikeBell(c, 2100, now + 0.11,  0.26, 1400);
  });
}

/** Play a sound file from a URL directly (used by the settings preview). */
export function playCustomUrl(url: string): void {
  playUrl(url);
}

export function isPrimed(): boolean { return primed; }
